import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  writeBatch,
  serverTimestamp,
  increment,
} from 'firebase/firestore';
import { db } from '../config/firebase-config';

// --- Collection helpers ---

const conversationsCol = (uid) =>
  collection(db, 'users', uid, 'conversations');

const conversationDoc = (uid, convId) =>
  doc(db, 'users', uid, 'conversations', convId);

const messagesCol = (uid, convId) =>
  collection(db, 'users', uid, 'conversations', convId, 'messages');

// --- Conversation CRUD ---

export const createConversation = async (uid, {
  assistantType,
  assistantTitle,
  apiPath,
  resourceId = null,
}) => {
  const docRef = await addDoc(conversationsCol(uid), {
    assistantType,
    assistantTitle,
    apiPath,
    title: 'New Conversation',
    titleGenerated: false,
    threadId: null,
    nhConversationId: null,
    resourceId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    messageCount: 0,
    lastMessagePreview: '',
    lastMessageAt: serverTimestamp(),
  });
  return docRef.id;
};

export const getConversation = async (uid, convId) => {
  const snap = await getDoc(conversationDoc(uid, convId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

export const updateConversation = async (uid, convId, updates) => {
  await updateDoc(conversationDoc(uid, convId), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};

export const deleteConversation = async (uid, convId) => {
  // Delete all messages first
  const msgSnap = await getDocs(messagesCol(uid, convId));
  const batch = writeBatch(db);
  msgSnap.docs.forEach((d) => batch.delete(d.ref));
  batch.delete(conversationDoc(uid, convId));
  await batch.commit();
};

// --- Message CRUD ---

export const addMessageBatch = async (uid, convId, messages) => {
  const batch = writeBatch(db);
  const convRef = conversationDoc(uid, convId);

  // Get current message count so we can set orderIndex
  const convSnap = await getDoc(convRef);
  let orderIndex = convSnap.exists() ? (convSnap.data().messageCount || 0) : 0;

  let lastText = '';
  messages.forEach((msg) => {
    const msgRef = doc(messagesCol(uid, convId));
    batch.set(msgRef, {
      sender: msg.sender,
      text: msg.text || '',
      timestamp: serverTimestamp(),
      orderIndex,
      images: msg.images || [],
      attachments: msg.attachments || [],
      isError: msg.isError || false,
    });
    lastText = msg.text || '';
    orderIndex++;
  });

  // Update conversation metadata
  batch.update(convRef, {
    messageCount: increment(messages.length),
    lastMessagePreview: lastText.substring(0, 120),
    lastMessageAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await batch.commit();
};

export const loadMessages = async (uid, convId, { messageLimit = 100, startAfterDoc = null } = {}) => {
  let q = query(
    messagesCol(uid, convId),
    orderBy('orderIndex', 'asc'),
    limit(messageLimit),
  );
  if (startAfterDoc) {
    q = query(q, startAfter(startAfterDoc));
  }
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// --- API reference updates ---

export const setThreadId = async (uid, convId, threadId) => {
  await updateConversation(uid, convId, { threadId });
};

export const setNhConversationId = async (uid, convId, nhConversationId) => {
  await updateConversation(uid, convId, { nhConversationId });
};

// --- Title ---

export const updateTitle = async (uid, convId, title) => {
  await updateConversation(uid, convId, { title, titleGenerated: true });
};

// --- Conversation listing (real-time) ---

export const subscribeToConversations = (uid, callback, { assistantType = null, maxResults = 50 } = {}) => {
  let q;
  if (assistantType) {
    q = query(
      conversationsCol(uid),
      where('assistantType', '==', assistantType),
      orderBy('updatedAt', 'desc'),
      limit(maxResults),
    );
  } else {
    q = query(
      conversationsCol(uid),
      orderBy('updatedAt', 'desc'),
      limit(maxResults),
    );
  }

  return onSnapshot(q, (snap) => {
    const conversations = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(conversations);
  }, (error) => {
    console.error('Error subscribing to conversations:', error);
    callback([]);
  });
};

// --- One-time fetch ---

export const getConversations = async (uid, { assistantType = null, maxResults = 30 } = {}) => {
  let q;
  if (assistantType) {
    q = query(
      conversationsCol(uid),
      where('assistantType', '==', assistantType),
      orderBy('updatedAt', 'desc'),
      limit(maxResults),
    );
  } else {
    q = query(
      conversationsCol(uid),
      orderBy('updatedAt', 'desc'),
      limit(maxResults),
    );
  }
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};
